import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { AssetSelect, useAssets } from '~/components/AssetSelect';
import {
  AssetTypeSelect,
  buildAssetTypeOptions,
  useAssetTypes,
} from '~/components/AssetTypeSelect';
import { Confirm } from '~/components/ui/Confirm';
import { HelperPopup } from '~/components/ui/HelperPopup';
import { ListAssetsQuery } from '~/generated/graphql';
import { unique } from '~/helpers/array';
import { notUndefined } from '~/helpers/filter';
import {
  AssetTaskSelection,
  Control,
  SetValue,
  Watch,
} from '../CustomTaskForm';
import { assetIsAtLocation } from './assetLocation';
import { filterAssetsByTypes } from './filterByAssetTypes';

type Asset = ListAssetsQuery['assets'][number];

type Props = {
  control: Control;
  locationId?: string;
  watch: Watch;
  setValue: SetValue;
  scopeIds?: string[];
  isJobTemplate?: boolean;
  // When true, keep specific asset selection visible but read-only (used for templates with specific assets)
  readOnlySpecificAssets?: boolean;
  /** Whether the assignee is a team member (null = no assignee selected) */
  assigneeIsTeamMember?: boolean | null;
  /** Disable specific asset selection, only allow asset type selection (for asset-based templates) */
  disableSpecificAssetSelection?: boolean;
  /** Whether this task is being edited in an asset-driven template context */
  isAssetDrivenTemplate?: boolean;
};

export function AssetTaskFields({
  control,
  locationId,
  watch,
  setValue,
  scopeIds,
  isJobTemplate = false,
  readOnlySpecificAssets = false,
  assigneeIsTeamMember = null,
  disableSpecificAssetSelection = false,
  isAssetDrivenTemplate = false,
}: Props) {
  const multipleLocations = scopeIds && scopeIds.length > 1;

  const [assets] = useAssets();
  const [globalAssetTypes] = useAssetTypes();

  const selection = watch('asset.selection');
  // Subscribe to spaces so that downstream components re-render when it changes
  watch('asset.spaces');
  const assetTypes = watch('asset.assetTypes');
  const selectedAssetIds = watch('asset.assets') as string[] | undefined;

  const specificAssetsSelected = selection === AssetTaskSelection.SPECIFIC;
  const hasSpecificAssets =
    specificAssetsSelected && (selectedAssetIds?.length ?? 0) > 0;

  // Check if this is the default rule (assetTypes must be null/undefined, not empty array)
  // Empty array means user hasn't selected types yet, null/undefined means default rule
  const isDefaultRule = useMemo(() => {
    return (
      isAssetDrivenTemplate &&
      selection === AssetTaskSelection.ALL &&
      assetTypes == null // Use loose null check to handle both undefined and null
    );
  }, [isAssetDrivenTemplate, selection, assetTypes]);

  // Confirmation state for mode switching
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<string | null>(null);

  const selectedLocationAssetTypes = useMemo(() => {
    return unique(
      assets
        .filter((asset) => {
          const deployment = asset.deployment;
          if (scopeIds?.length) {
            return scopeIds?.includes(deployment?.location.id ?? '');
          }
          return asset;
        })
        .map((item) => item.type)
    );
  }, [assets, scopeIds]);

  // Build options with immediate global fallback, then refine when assets are loaded
  const assetTypeOptions = useMemo(
    () =>
      buildAssetTypeOptions(
        globalAssetTypes,
        selectedLocationAssetTypes,
        isAssetDrivenTemplate
      ),
    [globalAssetTypes, selectedLocationAssetTypes, isAssetDrivenTemplate]
  );

  const filterByAssetTypes = useCallback(
    (assetsToFilter: Asset[]) => filterAssetsByTypes(assetsToFilter, assetTypes),
    [assetTypes]
  );

  const matchingAssetsCount = useMemo(() => {
    if (specificAssetsSelected) return 0;

    const locationAssets = assets.filter((asset) =>
      assetIsAtLocation(asset, locationId)
    );
    const filteredAssets = filterByAssetTypes(locationAssets);
    return filteredAssets.length;
  }, [specificAssetsSelected, assets, locationId, filterByAssetTypes]);

  // Auto-switch to "All Matching Assets" mode when:
  // 1. Specific asset selection is disabled (for asset-based templates)
  // 2. Multiple locations are selected (specific assets not available) - but only for regular jobs, not templates/automations
  // 3. Job template without specific assets (force configuration mode) - only if read-only
  // 4. Asset-driven templates: ALWAYS force specific mode when task has specific assets
  useEffect(() => {
    // Case 4: Asset-driven templates - force SPECIFIC mode when task has specific assets
    // This ensures tasks from asset-driven templates always show a specific asset, not "all matching assets"
    if (
      isAssetDrivenTemplate &&
      selectedAssetIds &&
      selectedAssetIds.length > 0
    ) {
      if (selection !== AssetTaskSelection.SPECIFIC) {
        setValue('asset.selection', AssetTaskSelection.SPECIFIC);
      }
      return; // Don't proceed with other auto-switch logic for asset-driven templates
    }

    if (selection === AssetTaskSelection.ALL) return; // Already in ALL mode

    // Case 1: Disable specific asset selection - force ALL mode (for asset-based templates)
    if (disableSpecificAssetSelection) {
      setValue('asset.selection', AssetTaskSelection.ALL);
      return;
    }

    // Case 2: Multiple locations - force ALL mode only for regular jobs (not templates/automations)
    // Templates and automations should allow specific asset selection regardless of location count
    if (multipleLocations && !isJobTemplate) {
      setValue('asset.selection', AssetTaskSelection.ALL);
      return;
    }

    // Case 3: Job template without specific assets - force ALL mode only if read-only
    // If readOnlySpecificAssets is false, allow user to select specific assets
    if (isJobTemplate && readOnlySpecificAssets) {
      const ids = selectedAssetIds ?? [];
      if (ids.length === 0) {
        setValue('asset.selection', AssetTaskSelection.ALL);
      }
    }
  }, [
    isAssetDrivenTemplate,
    disableSpecificAssetSelection,
    multipleLocations,
    isJobTemplate,
    readOnlySpecificAssets,
    selectedAssetIds,
    selection,
    setValue,
  ]);

  useEffect(() => {
    // When leaving specific-assets mode, clear any existing specific asset selections.
    // Important: Do NOT clear assetTypes here – those belong to "All Matching Assets" mode
    // and are intentionally controlled by the user. Clearing them here caused the
    // asset type selection to "blink" and immediately reset in asset type mode.
    if (!specificAssetsSelected && (selectedAssetIds?.length ?? 0) > 0) {
      setValue('asset.assets', []);
    }
  }, [specificAssetsSelected, selectedAssetIds, setValue]);

  const handleFiltersChange = useCallback(
    (filters: { places: string[]; spaces: string[]; assetTypes: string[] }) => {
      setValue('asset.spaces', filters.spaces);
      setValue('asset.assetTypes', filters.assetTypes);
    },
    [setValue]
  );

  // Check if current mode has selections that would be lost
  const hasCurrentSelections = useCallback(() => {
    if (specificAssetsSelected) {
      // In specific mode, check if assets are selected
      return (selectedAssetIds?.length ?? 0) > 0;
    } else {
      // In all matching mode, check if asset types are selected
      return assetTypes && assetTypes.length > 0;
    }
  }, [specificAssetsSelected, selectedAssetIds, assetTypes]);

  // Handle selection mode change with confirmation
  const handleSelectionModeChange = useCallback(
    (newSelection: string) => {
      if (newSelection === selection) return; // No change

      if (hasCurrentSelections()) {
        // Show confirmation dialog
        setPendingSelection(newSelection);
        setShowConfirm(true);
      } else {
        // No selections to lose, change immediately
        setValue('asset.selection', newSelection);
      }
    },
    [selection, hasCurrentSelections, setValue]
  );

  // Confirm the mode change and clear selections
  const handleConfirmModeChange = useCallback(() => {
    if (pendingSelection) {
      setValue('asset.selection', pendingSelection);
      // Clear relevant selections based on the mode we're leaving
      if (specificAssetsSelected) {
        // Leaving specific mode, clear assets
        setValue('asset.assets', []);
      } else {
        // Leaving all matching mode, clear asset types
        setValue('asset.assetTypes', []);
      }
    }
    setShowConfirm(false);
    setPendingSelection(null);
  }, [pendingSelection, specificAssetsSelected, setValue]);

  // Cancel the mode change
  const handleCancelModeChange = useCallback(() => {
    setShowConfirm(false);
    setPendingSelection(null);
  }, []);

  // For asset-based templates, don't show radio buttons or asset type selector here
  // (it's shown in Setup section instead)
  if (disableSpecificAssetSelection) {
    return null;
  }

  // For asset-driven templates with specific assets, disable mode switching
  const isModeSwitchingDisabled =
    isAssetDrivenTemplate && selectedAssetIds && selectedAssetIds.length > 0;

  return (
    <>
      <div className='mb-5 mt-5 flex flex-wrap items-center gap-5 gap-y-2'>
        <label className='flex items-center gap-2'>
          <input
            className='h-5 w-5 accent-brand'
            type='radio'
            value={AssetTaskSelection.SPECIFIC}
            checked={selection === AssetTaskSelection.SPECIFIC}
            onChange={() =>
              handleSelectionModeChange(AssetTaskSelection.SPECIFIC)
            }
            disabled={
              (multipleLocations && !isJobTemplate) || isModeSwitchingDisabled
            }
          />{' '}
          Specific Assets
        </label>
        <label className='flex items-center gap-2'>
          <input
            className='h-5 w-5 accent-brand'
            type='radio'
            value={AssetTaskSelection.ALL}
            checked={selection === AssetTaskSelection.ALL}
            onChange={() => handleSelectionModeChange(AssetTaskSelection.ALL)}
            disabled={
              (multipleLocations && !isJobTemplate) || isModeSwitchingDisabled
            }
          />{' '}
          All Matching Assets
        </label>
        {!isJobTemplate && (
          <span className='basis-full text-sm text-tertiary'>
            The specific assets option is only available in single property
            jobs.
          </span>
        )}
        {isModeSwitchingDisabled && (
          <span className='basis-full text-sm text-tertiary'>
            Asset-driven templates always use specific assets. Mode switching is
            disabled.
          </span>
        )}
      </div>

      {/* "All Matching Assets" mode - Keep original separate filter fields */}
      {!specificAssetsSelected && (
        <>
          {isDefaultRule ? (
            // For default rule task, show explanatory text instead of asset type selector
            <div className='border-blue-200 bg-blue-50 rounded-lg border p-4'>
              <div className='mb-2 flex items-start gap-2'>
                <div className='mt-0.5'>
                  <svg
                    className='text-blue-600 h-5 w-5'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='flex-1'>
                  <h4 className='text-blue-900 font-medium'>
                    Default Task Rule
                  </h4>
                  <p className='text-blue-800 mt-1 text-sm'>
                    This task will automatically be used for any asset with a
                    type that doesn&apos;t match the types specified in your
                    other task rules. This ensures all assets are covered even
                    when you add new asset types in the future.
                  </p>
                  <p className='text-blue-900 mt-2 text-sm font-medium'>
                    Targets: All asset types not covered by other rules
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Normal asset type selection
            <div className='flex items-start gap-2'>
              <div className='flex-1'>
                <Controller
                  name='asset.assetTypes'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <AssetTypeSelect
                      value={value ?? []}
                      onChange={onChange}
                      options={assetTypeOptions}
                    />
                  )}
                />
              </div>
              {isAssetDrivenTemplate &&
                (!assetTypes || assetTypes.length === 0) && (
                  <div className='pt-2'>
                    <HelperPopup>
                      Default rule: This task will be used for any asset with a
                      type that doesn&apos;t match a type in the task rules you
                      create. Leave asset types undefined to create a default
                      rule, or select specific types to narrow the scope.
                    </HelperPopup>
                  </div>
                )}
            </div>
          )}

          {!isJobTemplate && (
            <div className='mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm'>
              <div className='mb-2 font-medium text-neutral-700'>
                Asset Selection
              </div>

              <div className='text-neutral-600'>
                {!assetTypes?.length ? (
                  'Select at least one asset type to include assets in this task'
                ) : (
                  <>
                    {matchingAssetsCount === 0 && (
                      <span className='text-amber-600'>
                        No assets match the selected criteria
                      </span>
                    )}
                    {matchingAssetsCount === 1 && (
                      <span className='text-green-600'>
                        1 asset will be included in this task
                      </span>
                    )}
                    {matchingAssetsCount > 1 && (
                      <span className='text-green-600'>
                        {matchingAssetsCount} assets will be included in this
                        task
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {specificAssetsSelected && (
        <>
          <Controller
            name='asset.assets'
            control={control}
            render={({ field: { value, onChange } }) => {
              const selectedAssets = (value || [])
                .map((id: string) =>
                  assets.find((asset: Asset) => asset.id === id)
                )
                .filter(notUndefined);

              return (
                <div className='mb-3'>
                  <AssetSelect
                    assets={assets}
                    value={selectedAssets}
                    onChange={
                      readOnlySpecificAssets
                        ? () => {
                            // Read-only mode for templates with specific assets:
                            // keep current selection visible but prevent changes.
                          }
                        : (selected) => {
                            const selectedIds = selected.map(
                              (asset: Asset) => asset.id
                            );
                            onChange(selectedIds);
                          }
                    }
                    enableFilters={true}
                    defaultLocationId={
                      locationId?.includes(':')
                        ? locationId.split(':')[1]
                        : locationId
                    }
                    scopeIds={scopeIds}
                    mode='specific'
                    onFiltersChange={handleFiltersChange}
                    multiple={true}
                  />
                  {readOnlySpecificAssets && hasSpecificAssets && (
                    <p className='mt-1 text-xs text-secondary'>
                      Asset selection is read-only for this template because
                      changing it could affect existing repeat jobs, and
                      templates don&apos;t have a property context. To change
                      assets, adjust them on the automation and create a new
                      template.
                    </p>
                  )}
                </div>
              );
            }}
          />
        </>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <Confirm
          title='Clear current selection?'
          body={
            specificAssetsSelected
              ? "Switching to 'All Matching Assets' will clear your currently selected specific assets. Are you sure you want to continue?"
              : "Switching to 'Specific Assets' will clear your current asset type filters. Are you sure you want to continue?"
          }
          confirm='Yes, switch mode'
          cancel='Keep current mode'
          onConfirm={handleConfirmModeChange}
          onCancel={handleCancelModeChange}
        />
      )}

      {/* TODO: Add back in when full functionality is fully implemented */}
      {/*  <div className='my-5 flex flex-col gap-2'>
        <Label as='p'>Allow assignee to</Label>
        <Switch
          label='Edit assets'
          checked={watch('asset.allow')?.includes('edit')}
          onChange={(checked) => {
            const currentValues = watch('asset.allow') || [];
            if (checked && !currentValues.includes('edit')) {
              setValue('asset.allow', [...currentValues, 'edit']);
            } else if (!checked && currentValues.includes('edit')) {
              setValue(
                'asset.allow',
                currentValues.filter((v) => v !== 'edit')
              );
            }
          }}
        />
        <Switch
          label='Create new assets'
          checked={watch('asset.allow')?.includes('create')}
          onChange={(checked) => {
            const currentValues = watch('asset.allow') || [];
            if (checked && !currentValues.includes('create')) {
              setValue('asset.allow', [...currentValues, 'create']);
            } else if (!checked && currentValues.includes('create')) {
              setValue(
                'asset.allow',
                currentValues.filter((v) => v !== 'create')
              );
            }
          }}
        />
      </div> */}
    </>
  );
}
